import Layout from "@/components/Layout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Car, Plane, Shield, Clock, Users, Award, Star, CheckCircle, Heart, Target, Globe, MapPin, Phone, Mail } from "lucide-react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import dominicanSolLogo from "@/assets/dominican-sol-logo.png";
import { useSiteConfig } from "@/hooks/useSiteConfig";

const About = () => {
  const { getConfig } = useSiteConfig();
  
  const stats = getConfig('about_stats', [
    {
      icon: Star,
      number: "10+",
      label: "Years of Experience",
      description: "Serving Punta Cana travelers"
    },
    {
      icon: Users,
      number: "5,000+",
      label: "Happy Customers",
      description: "Satisfied clients worldwide"
    },
    {
      icon: Award,
      number: "4.9/5",
      label: "Average Rating",
      description: "Based on verified reviews"
    },
    {
      icon: CheckCircle,
      number: "24/7",
      label: "Customer Support",
      description: "Always here for you"
    }
  ]);

  const values = getConfig('about_values', [
    {
      icon: Shield,
      title: "Safety First",
      description: "Your safety is our top priority. All our vehicles are regularly inspected and maintained, and our drivers are fully licensed and insured."
    },
    {
      icon: Heart,
      title: "Customer Excellence",
      description: "We're dedicated to providing exceptional service that exceeds your expectations. Your satisfaction is our success."
    },
    {
      icon: Target,
      title: "Reliability",
      description: "Count on us for punctual service, transparent pricing, and consistent quality. We're committed to making your journey stress-free."
    },
    {
      icon: Globe,
      title: "Local Expertise",
      description: "As locals, we know Punta Cana and the Dominican Republic inside out. Let us share our knowledge and passion with you."
    }
  ]);

  const team = getConfig('about_team', [
    {
      name: "Professional Drivers",
      role: "Bilingual & Licensed",
      description: "Our drivers are carefully selected, professionally trained, and fluent in English and Spanish."
    },
    {
      name: "Customer Service Team",
      role: "24/7 Available",
      description: "Our dedicated support team is always ready to assist you before, during, and after your journey."
    },
    {
      name: "Tour Guides",
      role: "Local Experts",
      description: "Passionate about sharing the beauty and culture of the Dominican Republic with our guests."
    }
  ]);

  const whyChooseUs = getConfig('about_why_items', [
    "Licensed and fully insured operations",
    "24/7 bilingual customer support",
    "Professional and experienced drivers",
    "Modern, clean, and comfortable fleet",
    "Transparent pricing with no hidden fees",
    "Instant booking confirmation",
    "Flexible cancellation policies",
    "Flight tracking for airport pickups"
  ]);

  return (
    <Layout>
      <Helmet>
        <title>About Us | Dominican Sol Transfers - Your Premier Transfer & Tour Service</title>
        <meta name="description" content="Learn about Dominican Sol Transfers - your trusted partner for airport transfers and tours in Punta Cana and throughout the Dominican Republic." />
      </Helmet>

      {/* Hero Section */}
      <section className="relative py-32 bg-gradient-to-br from-primary/10 via-background to-accent/10 overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1544551763-46a013bb70d5?auto=format&fit=crop&q=80')] bg-cover bg-center opacity-5"></div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="mb-8 flex justify-center">
              <img src={dominicanSolLogo} alt="Dominican Sol Transfers" className="h-24 w-auto" />
            </div>
            <Badge className="mb-6 bg-secondary text-white text-lg py-2 px-6">
              {getConfig('about_hero_badge', 'Your Premier Transfer & Tour Service')}
            </Badge>
            <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-primary via-secondary to-accent bg-clip-text text-transparent">
              {getConfig('about_hero_title', 'About Dominican Sol Transfers')}
            </h1>
            <p className="text-xl text-muted-foreground leading-relaxed">
              {getConfig('about_hero_description', "Experience the Caribbean with comfort, reliability, and style. We're your trusted partner for seamless travel in Punta Cana and throughout the Dominican Republic.")}
            </p>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gradient-to-b from-background to-primary/5">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <Card key={index} className="text-center border-primary/20 hover:border-primary/40 transition-all hover:shadow-lg">
                  <CardContent className="pt-8">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-secondary to-tropical mb-4">
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                    <div className="text-4xl font-bold text-primary mb-2">{stat.number}</div>
                    <div className="text-lg font-semibold mb-1">{stat.label}</div>
                    <div className="text-sm text-muted-foreground">{stat.description}</div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="mb-4 bg-accent text-white">{getConfig('about_story_badge', 'Our Story')}</Badge>
              <h2 className="text-4xl font-bold mb-6 text-primary">
                {getConfig('about_story_title', 'Connecting Travelers to Caribbean Paradise')}
              </h2>
              <div className="space-y-4 text-muted-foreground leading-relaxed">
                <p>
                  {getConfig('about_story_paragraph_1', 'Dominican Sol Transfers was born from a passion for hospitality and a deep love for the Dominican Republic. Founded over a decade ago, we started with a simple mission: to provide travelers with safe, comfortable, and reliable transportation in Punta Cana.')}
                </p>
                <p>
                  {getConfig('about_story_paragraph_2', "What began as a small transfer service has grown into a comprehensive travel solution, serving thousands of satisfied customers from around the world. Our commitment to excellence and personalized service has made us a trusted name in Dominican tourism.")}
                </p>
                <p>
                  {getConfig('about_story_paragraph_3', "Today, we're proud to offer not just airport transfers, but also unforgettable tours that showcase the natural beauty, rich culture, and warm hospitality that make the Dominican Republic a world-class destination.")}
                </p>
              </div>
            </div>
            <div className="relative">
              <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://images.unsplash.com/photo-1506929562872-bb421503ef21?auto=format&fit=crop&q=80" 
                  alt="Punta Cana Beach"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-gradient-to-br from-secondary to-tropical text-white p-6 rounded-xl shadow-xl max-w-xs">
                <div className="flex items-center gap-3 mb-2">
                  <MapPin className="w-6 h-6" />
                  <span className="font-bold text-lg">Based in Punta Cana</span>
                </div>
                <p className="text-sm opacity-90">Serving all of Dominican Republic</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision Section */}
      <section className="py-20 bg-gradient-to-br from-primary/5 to-accent/5">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <Card className="border-2 border-primary/20 hover:border-primary/40 transition-all">
              <CardHeader>
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center mb-4">
                  <Target className="w-7 h-7 text-white" />
                </div>
                <CardTitle className="text-2xl text-primary">{getConfig('about_mission_title', 'Our Mission')}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground leading-relaxed">
                  {getConfig('about_mission_text', "To provide safe, reliable, and comfortable transportation and tour services that exceed our customers' expectations, while showcasing the beauty and culture of the Dominican Republic with professionalism and care.")}
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 border-secondary/20 hover:border-secondary/40 transition-all">
              <CardHeader>
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-secondary to-tropical flex items-center justify-center mb-4">
                  <Globe className="w-7 h-7 text-white" />
                </div>
                <CardTitle className="text-2xl text-secondary">{getConfig('about_vision_title', 'Our Vision')}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground leading-relaxed">
                  {getConfig('about_vision_text', 'To be the most trusted and preferred transfer and tour service in the Dominican Republic, known for exceptional customer service, reliability, and creating unforgettable travel experiences.')}
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Core Values Section */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-tropical text-white text-lg py-2 px-6">{getConfig('about_values_badge', 'Our Values')}</Badge>
            <h2 className="text-4xl font-bold mb-4 text-primary">{getConfig('about_values_title', 'What Drives Us')}</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              {getConfig('about_values_description', 'These core values guide everything we do, from the moment you book to the end of your journey.')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <Card key={index} className="border-2 border-transparent hover:border-accent transition-all hover:shadow-xl group">
                  <CardHeader>
                    <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-accent/20 to-primary/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                      <Icon className="w-8 h-8 text-primary" />
                    </div>
                    <CardTitle className="text-xl">{value.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground text-sm leading-relaxed">
                      {value.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 bg-gradient-to-b from-primary/5 to-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-primary text-white text-lg py-2 px-6">{getConfig('about_team_badge', 'Our Team')}</Badge>
            <h2 className="text-4xl font-bold mb-4 text-primary">{getConfig('about_team_title', 'Meet the People Behind Your Journey')}</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              {getConfig('about_team_description', 'Our dedicated team is committed to making your Dominican Republic experience exceptional.')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {team.map((member, index) => (
              <Card key={index} className="text-center border-primary/20 hover:border-primary/40 transition-all hover:shadow-lg">
                <CardHeader>
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-secondary to-tropical mx-auto mb-4 flex items-center justify-center">
                    <Users className="w-10 h-10 text-white" />
                  </div>
                  <CardTitle className="text-xl text-primary">{member.name}</CardTitle>
                  <CardDescription className="text-secondary font-semibold">{member.role}</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    {member.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <Badge className="mb-4 bg-accent text-white text-lg py-2 px-6">{getConfig('about_why_badge', 'Why Choose Us')}</Badge>
              <h2 className="text-4xl font-bold mb-4 text-primary">
                {getConfig('about_why_title', 'The Dominican Sol Difference')}
              </h2>
              <p className="text-muted-foreground">
                {getConfig('about_why_description', "Here's what sets us apart from other transfer and tour services")}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {whyChooseUs.map((item, index) => (
                <div key={index} className="flex items-start gap-3 p-4 rounded-lg hover:bg-primary/5 transition-colors">
                  <CheckCircle className="w-6 h-6 text-secondary flex-shrink-0 mt-0.5" />
                  <span className="text-muted-foreground">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-primary to-accent text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1519046904884-53103b34b206?auto=format&fit=crop&q=80')] bg-cover bg-center opacity-10"></div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              {getConfig('about_cta_title', 'Ready to Experience Dominican Sol Service?')}
            </h2>
            <p className="text-xl mb-8 opacity-90">
              {getConfig('about_cta_description', 'Book your transfer or tour today and discover why thousands of travelers trust us')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="bg-white text-primary hover:bg-white/90 text-lg px-8">
                <Link to="/transfers">Book a Transfer</Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-primary text-lg px-8">
                <Link to="/tours">Explore Tours</Link>
              </Button>
            </div>

            <div className="mt-12 pt-12 border-t border-white/20">
              <p className="text-lg mb-6 font-semibold">Get in Touch</p>
              <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
                <a href={`tel:${getConfig('about_contact_phone', '+1 (809) 123-4567')}`} className="flex items-center gap-2 hover:opacity-80 transition-opacity">
                  <Phone className="w-5 h-5" />
                  <span>{getConfig('about_contact_phone', '+1 (809) 123-4567')}</span>
                </a>
                <a href={`mailto:${getConfig('about_contact_email', 'info@dominicansoltransfers.com')}`} className="flex items-center gap-2 hover:opacity-80 transition-opacity">
                  <Mail className="w-5 h-5" />
                  <span>{getConfig('about_contact_email', 'info@dominicansoltransfers.com')}</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default About;
